import { login, logout, getInfo } from "@/modules/huyu-framework/backend/api/login";
import { getEncryptKey, encryptPassword } from "@/modules/huyu-framework/backend/utils/encrypt";
import { getToken, setToken, removeToken, setUserId } from "@/modules/huyu-framework/backend/utils/auth";
import defAva from "@/modules/huyu-framework/backend/assets/images/profile.jpg";
import { clearLocalCache } from "@/modules/huyu-framework/backend/utils/clientCache";

const useUserStore = defineStore("user", {
  state: () => ({
    token: getToken(),
    id: "",
    name: "",
    avatar: "",
    roles: [],
    permissions: [],
    user: {},
  }),
  actions: {
    // 登录
    login(userInfo) {
      const username = userInfo.username.trim();
      const password = userInfo.password;
      const code = userInfo.code;
      const uuid = userInfo.uuid;
      return new Promise((resolve, reject) => {
        // 获取服务器动态密钥并加密密码（keyId 非空表示密文登录）
        // 只在取密钥失败时降级；登录本身失败必须直接抛出，
        // 否则会被这个 catch 当成密钥失败而重发一次登录，导致重复弹窗。
        getEncryptKey()
          .catch((keyError) => {
            console.warn("加密密钥获取失败，降级为普通登录", keyError);
            return null;
          })
          .then((keyRes) => {
            if (keyRes && keyRes.key) {
              const encryptPwd = encryptPassword(password, keyRes.key);
              return login(username, encryptPwd, code, uuid, keyRes.keyId);
            }
            return login(username, password, code, uuid, null);
          })
          .then((res) => {
            setToken(res.token);
            this.token = res.token;
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    // 获取用户信息
    getInfo() {
      return new Promise((resolve, reject) => {
        getInfo()
          .then((res) => {
            localStorage.setItem("projects", JSON.stringify(res.projects));

            const account = res.user.userName;
            const projects = res.projects || [];
            if (projects.length === 0) {
              throw new Error(`账号 ${account} 未关联任何项目，无法进入后台`);
            }
            const toProjectId = (value) => {
              if (value === null || value === undefined || value === "") {
                return null;
              }
              const id = Number(value);
              return Number.isNaN(id) ? null : id;
            };
            const findProject = (id) =>
              projects.find((item) => Number(item.projectId) === id);

            // 目标项目优先级：上次使用（缓存）→ 登录页默认项目（项目配置/URL）→ 后端返回的第一个项目
            let targetProjectId = null;
            let lastProjectMap = {};
            try {
              lastProjectMap = JSON.parse(
                localStorage.getItem("lastProject") || "{}"
              );
            } catch (e) {
              lastProjectMap = {};
            }
            const savedProjectId = toProjectId(lastProjectMap[account]);
            if (savedProjectId !== null && findProject(savedProjectId)) {
              targetProjectId = savedProjectId;
            }
            if (targetProjectId === null) {
              const loginProjectId = toProjectId(localStorage.getItem("loginId"));
              if (loginProjectId !== null && findProject(loginProjectId)) {
                targetProjectId = loginProjectId;
              }
            }
            if (targetProjectId === null) {
              targetProjectId = Number(projects[0].projectId);
            }

            const value = res.modules.filter(
              (item) => Number(item.projectId) === targetProjectId
            );
            localStorage.setItem("projectId", String(targetProjectId));

            console.log("获取用户信息", res, value);
            localStorage.setItem("modules", JSON.stringify(value));
            localStorage.setItem("moduleList", JSON.stringify(value));

            const user = res.user;
            const avatar =
              user.avatar == "" || user.avatar == null
                ? defAva
                : import.meta.env.VITE_APP_BASE_API + user.avatar;
            // console.log(res);
            if (res.roles && res.roles.length > 0) {
              // 验证返回的roles是否是一个非空数组
              this.roles = res.roles;
              // console.log(res);

              this.permissions = res.permissions;
            } else {
              this.roles = ["ROLE_DEFAULT"];
            }
            this.permissions = res.permissions;
            this.id = user.userId;
            this.name = user.userName;
            this.avatar = avatar;
            this.user = user;
            // 存储 userId 到 Cookies，供 getListByUserApi 等接口使用
            setUserId(user.userId);
            resolve(res);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    // 退出系统
    logOut() {
      return new Promise((resolve, reject) => {
        logout(this.token)
          .then(() => {
            const account = this.name;
            const currentProjectId = localStorage.getItem("projectId");
            let lastProjectMap = {};
            if (account && currentProjectId) {
              try {
                lastProjectMap = JSON.parse(
                  localStorage.getItem("lastProject") || "{}"
                );
              } catch (e) {
                lastProjectMap = {};
              }
              lastProjectMap[account] = currentProjectId;
            }
            clearLocalCache();
            // clearLocalCache 会一并清掉 lastProject，故在清空后回写，保留「上次使用项目」
            if (account && currentProjectId) {
              localStorage.setItem(
                "lastProject",
                JSON.stringify(lastProjectMap)
              );
            }
            this.token = "";
            this.roles = [];
            this.permissions = [];
            removeToken();
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
  },
});

export default useUserStore;
